import { useEffect } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'

/**
 * Hook personalizado para manejar la PWA y auto-actualización del Service Worker
 *
 * @hook
 * @description
 * Registra el Service Worker y configura la auto-actualización automática.
 * Verifica nuevas versiones cada 5 minutos y actualiza silenciosamente sin intervención del usuario.
 *
 * @param {Object} options - Opciones de configuración
 * @param {number} [options.updateInterval=300000] - Intervalo de verificación en ms (default: 5 minutos)
 * @param {boolean} [options.autoUpdate=true] - Activar auto-actualización automática
 * @param {Function} [options.onUpdateFound] - Callback cuando se encuentra una actualización
 * @param {Function} [options.onUpdateError] - Callback cuando hay error en la actualización
 *
 * @returns {Object} Estado y métodos del Service Worker
 * @returns {boolean} return.needRefresh - Indica si hay una nueva versión disponible
 * @returns {Function} return.updateServiceWorker - Función para forzar actualización manual
 * @returns {boolean} return.offlineReady - Indica si la app está lista para funcionar offline
 *
 * @example
 * ```jsx
 * function App() {
 *   const { needRefresh, updateServiceWorker, offlineReady } = usePWA({
 *     updateInterval: 60000, // 1 minuto
 *     onUpdateFound: () => console.info('Nueva versión disponible!')
 *   });
 *
 *   return <div>App content</div>;
 * }
 * ```
 */
export function usePWA(options = {}) {
  const {
    updateInterval = 60000 * 5, // 5 minutos por defecto
    autoUpdate = true,
    onUpdateFound,
    onUpdateError,
  } = options

  // En desarrollo, mostrar mensaje informativo sobre PWA (solo una vez)
  useEffect(() => {
    if (import.meta.env.DEV) {
      // Usar sessionStorage para evitar logs duplicados
      const PWA_INFO_SHOWN = 'pwa_info_shown'
      if (!sessionStorage.getItem(PWA_INFO_SHOWN)) {
        console.info('ℹ️ PWA/Service Worker deshabilitado en desarrollo')
        console.info('💡 Se activará automáticamente en producción')
        sessionStorage.setItem(PWA_INFO_SHOWN, 'true')
      }
    }
  }, [])

  const {
    needRefresh: [needRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration) {
      console.info('✅ Service Worker registrado exitosamente')
      console.info('📱 PWA instalada y lista para funcionar offline')

      // Configurar verificación periódica de actualizaciones
      if (registration && updateInterval > 0) {
        const intervalId = setInterval(() => {
          console.info('🔄 Verificando actualizaciones...')
          registration.update()
        }, updateInterval)

        // Limpiar interval cuando el componente se desmonte
        return () => clearInterval(intervalId)
      }
    },

    onRegisterError(error) {
      // Silenciar errores de SSL en desarrollo (certificados autofirmados)
      const isSSLError =
        error?.message?.includes('SSL certificate error') || error?.name === 'SecurityError'
      const isDev = import.meta.env.DEV

      if (isSSLError && isDev) {
        console.debug('⚠️ Service Worker deshabilitado en desarrollo (certificado SSL autofirmado)')
        console.debug('✅ Esto es normal y no afecta la funcionalidad')
        console.debug('💡 En producción funcionará correctamente con SSL válido')
        return
      }

      // Solo mostrar errores reales que no sean SSL en dev
      console.error('❌ Error al registrar Service Worker:', error)

      // Callback personalizado de error
      if (onUpdateError) {
        onUpdateError(error)
      }
    },

    onNeedRefresh() {
      console.info('🆕 Nueva versión disponible')

      // Callback personalizado cuando se encuentra actualización
      if (onUpdateFound) {
        onUpdateFound()
      }
    },

    onOfflineReady() {
      console.info('📴 App lista para funcionar offline')
      setOfflineReady(true)
    },
  })

  // Auto-actualización automática cuando se detecta nueva versión
  useEffect(() => {
    if (needRefresh && autoUpdate) {
      console.info('⚡ Actualizando a la nueva versión...')
      updateServiceWorker(true)
    }
  }, [needRefresh, autoUpdate, updateServiceWorker])

  // Métodos y estado expuestos
  return {
    needRefresh,
    offlineReady,
    updateServiceWorker,
    // Método helper para forzar actualización
    forceUpdate: () => {
      console.info('🔄 Forzando actualización manual...')
      updateServiceWorker(true)
    },
  }
}

export default usePWA
